import AddIcon from '@mui/icons-material/Add';
import { Grid, Link, Stack, Box, List, ListItemButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  IUserDTO,
  IWorkspace,
  getWorkspaceUsers,
  IOrganizationalUnitManagers,
  createOrganizationalUnitManagers,
  selectedOrganizationalUnitManagers,
  deleteOUManagerMember,
} from 'wisestep-api';
import {
  WsTypography,
  WsButton,
  WsCard,
  CardVariant,
  Item,
  EmptyTeamsAndUsersIcon,
  WsTypoVariants,
  WsDrawer,
  WsTypoColors,
  WsIconMoreVer,
} from 'wisestep-base';

import PersonAvatar from '../../../images/Avatar/Person.svg';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getIdByEmail } from '../../candidates/utils/CandidateHelper';
import PopoverOnClick from '../../clientListingPage/components/PopoverOnClick';
import { StyledPaginationBox } from '../../clientsSettings/components/OrganizationalUnitLayoutNew';
import AccountManagersList from '../../jobs/assign/components/AccountManagersList';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';

interface IAllManagersInWorkspaceList {
  isOrganizationalUnit?: boolean;
  addButtonText?: string;
  organizationalUnitId?: string;
  onFetch?: () => void;
  getselectedManagers?: any;
  isManagerOrganizationalUnit?: boolean;
}

const AllManagersInWorkspaceList = ({
  addButtonText,
  organizationalUnitId,
  onFetch,
  getselectedManagers,
  isManagerOrganizationalUnit,
}: IAllManagersInWorkspaceList) => {
  const [selectedMembers, setSelectedMembers] = useState<IWorkspace[]>([]);
  const recruiters = useRef<IUserDTO[]>();
  const [managersOpen, setmanagersOpen] = useState(false);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const reload = () => setmanagersOpen(false);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const data = getselectedManagers?.map((item) => ({
    id: item.userId,
    organizationalUnitId: organizationalUnitId,
    name: item.userName ?? 'NA',
    email: item.userId,
    role: item.role,
  }));

  const handleDeleteManagerMember = useCallback(
    async (rowObj: any) => {
      const newresp = await selectedOrganizationalUnitManagers(rowObj.organizationalUnitId ?? '');
      newresp.data.forEach(async (item) => {
        if (rowObj.organizationalUnitId === item.organizationalId) {
          if (
            rowObj.email === item.accountManagerId &&
            (item.type === 'Account Managers' || item.type === null)
          ) {
            const payload = item.value;
            const res = await deleteOUManagerMember(payload);
            if (res.status === 200) {
              setNotification(
                success('Account Manager is removed from organization successfully!!')
              );
              onFetch && onFetch();
            } else {
              setNotification(warning('Something went wrong!!'));
            }
          }
        }
      });
    },
    [onFetch, setNotification]
  );

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        headerName: 'Account Manager Name',
        field: 'name',
        flex: 1,
        renderCell: (param) => (
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <WsAvatar src={PersonAvatar} variant="circular" />
            </Grid>
            <Grid item>
              <Link
                sx={{ cursor: 'pointer' }}
                underline="none"
                onClick={() =>
                  getIdByEmail(
                    param.value?.toLowerCase(),
                    recruiters.current?.find((user) => user.email === param.value?.toLowerCase())
                  )
                }
              >
                <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY750}>
                  {param.value}
                </WsTypography>
              </Link>
            </Grid>
          </Grid>
        ),
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 1,
        renderCell: (param) => (
          <WsTypography variant={WsTypoVariants.BODY1}>{param.value}</WsTypography>
        ),
      },
      {
        headerName: 'Role',
        field: 'role',
        flex: 1,
        renderCell: (param) => (
          <WsTypography variant={WsTypoVariants.BODY1}>{param.value}</WsTypography>
        ),
      },
    ];

    if (!isManagerOrganizationalUnit) {
      baseColumns.push({
        headerName: '',
        field: 'actions',
        width: 50,
        renderCell: (param) => (
          <PopoverOnClick element={<WsIconMoreVer />}>
            <List sx={{ borderRadius: 5, width: 150 }}>
              <ListItemButton onClick={() => handleDeleteManagerMember(param.row)}>
                <WsTypography
                  variant={WsTypoVariants.SUB_TITLE}
                  cursor="pointer"
                  color={WsTypoColors.RED}
                >
                  Remove Item
                </WsTypography>
              </ListItemButton>
            </List>
          </PopoverOnClick>
        ),
      });
    }

    return baseColumns;
  }, [handleDeleteManagerMember, isManagerOrganizationalUnit]);

  useEffect(() => {
    const getInvitedUsers = async () => {
      const response = await selectedOrganizationalUnitManagers(organizationalUnitId ?? '');
      const idsSelected: string[] = response.data.map((item) => item.accountManagerId);
      if (idsSelected && idsSelected.length > 0) {
        const query = {
          roles: [],
          statuses: ['all'],
          userIds: idsSelected,
          page: 0,
          size: 50,
        };
        const req = await getWorkspaceUsers(query);
        const selectedData = req.data.workspaceUsers;
        setSelectedMembers(selectedData);
      } else {
        setSelectedMembers([]);
      }
    };
    getInvitedUsers();
  }, [organizationalUnitId]);

  const handleDrawerOpen = async () => {
    setmanagersOpen(true);
  };

  const addManagersOrganizationalUnit = async (data) => {
    if (organizationalUnitId) {
      const req: IOrganizationalUnitManagers[] = data.managersList.map((item) => {
        return {
          organizationalId: organizationalUnitId,
          accountManagerId: item,
          type: 'Account Managers',
        };
      });
      await createOrganizationalUnitManagers(req);
    }
    setmanagersOpen(false);
    setNotification(success(`Managers has been added successfully !!`));
    fetchData();
  };

  const fetchData = async () => {
    onFetch && onFetch();
  };

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {data.length}
        </WsTypography>
      </Box>
      {data.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(data.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid container mt={2} justifyContent={'space-between'}>
      <Grid container item xs={12}>
        {!isManagerOrganizationalUnit && (
          <Box marginLeft="auto">
            <WsButton
              startIcon={<AddIcon />}
              onClick={handleDrawerOpen}
            >{`Add ${addButtonText}`}</WsButton>
          </Box>
        )}
        <Grid item xs={12}>
          {isEmpty(getselectedManagers) ? (
            <WsCard
              title={''}
              sx={{ mt: 4 }}
              isButton={false}
              cardType={CardVariant.WITH_LIGHT_BG}
              middleComponent={
                <Item>
                  <Stack alignItems="center" spacing={3}>
                    <EmptyTeamsAndUsersIcon />
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>Its lonely here</WsTypography>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                      Add Account managers from your workspace.
                    </WsTypography>
                  </Stack>
                </Item>
              }
            />
          ) : (
            <>
              {paginationComponent}
              <StyledDataGrid
                autoHeight
                hideFooter
                columns={columns}
                rows={data}
                page={pageNo}
                pageSize={pageSize}
              />
              {data.length > 10 && paginationComponent}
            </>
          )}
        </Grid>
      </Grid>
      <WsDrawer
        open={managersOpen}
        onClose={reload}
        title={'Select Account Managers'}
        headerSize="medium"
        size="small"
      >
        <AccountManagersList
          onInput={(data) => {
            addManagersOrganizationalUnit(data);
          }}
          selectedManager={selectedMembers}
        />
      </WsDrawer>
    </Grid>
  );
};

export default AllManagersInWorkspaceList;
